import React, { useState } from 'react';
import { createUserWithEmailAndPassword, signInWithPopup, updateProfile } from 'firebase/auth';
import { auth, googleProvider } from './../../firebaseConfig'; // Firebase Auth and Google Provider
import Input from './../ui/Input';
import Button from './../ui/Button';
import './SignUp.css'; // Import CSS for styling
import GoogleIcon from '../../assets/logos/google.svg'; // Import Google icon

interface SignUpProps {
  onSwitch: () => void;
}

const SignUp: React.FC<SignUpProps> = ({ onSwitch }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Handle Email/Password Sign-Up
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }

    try {
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update user's display name
      await updateProfile(user, {
        displayName: `${firstName} ${lastName}`,
      });

      console.log('User signed up successfully:', user);
    } catch (err: any) {
      setError(err.message);
      console.error('Sign-up error:', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle Google Sign-Up
  const handleGoogleSignUp = async () => {
    setError(null);
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      console.log('User signed up with Google:', user);
    } catch (err: any) {
      setError(err.message);
      console.error('Google Sign-up error:', err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <h2 className="signup-title">Create new Account</h2>
      <p className="signup-subtext">
        If you already have an account, please{' '}
        <span onClick={onSwitch} className="link">
          Login
        </span>
      </p>
      <form onSubmit={handleSignUp} className="signup-form">
        <Input
          type="text"
          placeholder="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <Input
          type="text"
          placeholder="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <Input
          type="email"
          placeholder="Your Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Input
          type="password"
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        {error && <p className="error-message">{error}</p>}
        <Button type="submit" width="100%" disabled={loading}>
          {loading ? 'Signing Up...' : 'Sign Up'}
        </Button>
      </form>
      <div className="divider">
        <span>Or</span>
      </div>
      <Button
        type="button"
        variant="outline"
        width="100%"
        onClick={handleGoogleSignUp}
        disabled={loading}
      >
        <img src={GoogleIcon} alt="Google Icon" className="google-icon" />
        {loading ? 'Loading...' : 'Sign Up with Google'}
      </Button>
    </div>
  );
};

export default SignUp;
